"use client";

import { stegaClean } from "@sanity/client/stega";
import { cn } from "@/lib/utils";
import { IEventHostFact, PageEventSchema } from "@/sanity/pages/PageEventTemplate";
import { TicketCTA, type EventLayout } from "@/components/pageComponents/event/EventShared";

/** Figma fixes each host-fact's text width so every row wraps to two lines (desktop). */
const HOST_FACT_WIDTHS = [
  "lg:max-w-[45.5rem]",
  "max-w-[38.375rem] lg:max-w-[53rem]",
  "max-w-[38.25rem] lg:max-w-none",
  "lg:max-w-[62.625rem]",
  "max-w-[34.875rem] lg:max-w-[54.5rem]",
  "max-w-[35.625rem] lg:max-w-none",
];

/** One host fact: strong children render black/medium, the rest muted. */
function HostFactText({ blocks, className }: { blocks?: IEventHostFact["text"]; className?: string }) {
  if (!blocks?.length) return null;
  return (
    <p className={cn("mb-0 flex-1 font-geist text-[2.25rem] leading-[1.5] font-normal", className)}>
      {blocks.map((block, bi) =>
        (block.children as { _key?: string; text?: string; marks?: string[] }[] | undefined)?.map((child, ci) => (
          <span
            key={child._key ?? `${bi}-${ci}`}
            className={child.marks?.includes("strong") ? "font-medium text-black" : "text-black/70"}>
            {child.text}
          </span>
        )),
      )}
    </p>
  );
}

/** Host section: badge + title, portrait, fact list, CTA. */
export function EventHost({ data, mobileImageSrc, layout = "event" }: { data: PageEventSchema; mobileImageSrc?: string; layout?: EventLayout }) {
  const monthly = layout === "monthly";
  const hostFacts = data.s4Items ?? [];

  return (
    <section className={cn("w-full rounded-[1.375rem] bg-[#f3f3f3] px-[2rem] py-[7rem] lg:py-[15rem]", monthly && "lg:min-h-[122.1375rem] max-lg:min-h-[calc(156.25*var(--page-rem))] max-lg:px-[calc(2*var(--page-rem))] max-lg:py-[calc(7*var(--page-rem))]")}>
      <div className={cn("mx-auto flex w-full flex-col gap-[2.25rem] lg:w-[150rem] lg:flex-row lg:items-center lg:justify-between lg:gap-0", monthly && "max-lg:w-[min(calc(49.75*var(--page-rem)),100%)] max-lg:gap-[calc(2.25*var(--page-rem))]")}>
        {/* Badge + title (mobile order: above the portrait) */}
        <div className={cn("flex flex-col items-center gap-[2.5rem] text-center lg:hidden", monthly && "max-lg:gap-[calc(2.5*var(--page-rem))]")}>
          {data.s4BadgeText && (
            <div className={cn("flex w-fit items-center gap-[1.25rem] border border-black/[0.04] bg-black/[0.04] px-[2.5rem] py-[1.5rem]", monthly && "py-0 lg:h-[4.25rem] lg:px-[calc(2.5rem-1px)] max-lg:h-[calc(4.25*var(--page-rem))] max-lg:px-[calc(2.5*var(--page-rem)-1px)] max-lg:gap-[calc(1.25*var(--page-rem))]")}>
              <span className="h-[0.75rem] w-[0.75rem] rounded-full bg-[#1b8869]" />
              <p className={cn("mb-0 font-geist text-[1.75rem] leading-[1.15] font-medium whitespace-nowrap text-black/[0.64] uppercase", monthly && "[text-box-trim:trim-both] [text-box-edge:cap_alphabetic] max-lg:text-[calc(1.75*var(--page-rem))]")}>
                {data.s4BadgeText}
              </p>
            </div>
          )}
          {(data.s4TitleRegular || data.s4TitleItalic) && (
            <h2 className={cn("mb-0 w-[45rem] font-blacker text-[4.5rem] leading-[1.15] font-normal tracking-[-0.225rem] text-black [&_span]:font-blacker", monthly && "max-lg:w-[min(calc(44.875*var(--page-rem)),100%)] max-lg:text-[calc(4.5*var(--page-rem))] max-lg:tracking-[calc(-0.3*var(--page-rem))]")}>
              {data.s4TitleRegular && <span>{data.s4TitleRegular} </span>}
              {data.s4TitleItalic && (
                <span className={cn("leading-none tracking-[-0.135rem] text-[#3a5842] italic", monthly && "max-lg:tracking-[inherit] max-lg:leading-[1.15]")}>{data.s4TitleItalic}</span>
              )}
            </h2>
          )}
        </div>

        {/* Portrait (mobile: between title and facts) */}
        <div className={cn("w-full rounded-[1rem] border border-black/[0.16] p-[0.75rem] lg:order-2 lg:w-auto", monthly && "max-lg:p-[calc(0.8125*var(--page-rem)-1px)]")}>
          <div className="relative aspect-[385/350] w-full overflow-clip rounded-[0.5rem] bg-gradient-to-b from-[#184236] to-[#a7bfb8] lg:h-[90.375rem] lg:w-[68.875rem]">
            {data.s4Image?.src && (
              <picture className="contents">
                {mobileImageSrc && <source media="(max-width: 992px)" srcSet={mobileImageSrc} />}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={data.s4Image.src}
                  alt={data.s4TitleItalic ?? ""}
                  className={cn("absolute inset-0 h-full w-full object-cover object-top", monthly && stegaClean(data.s4Image.src) === "/monthly-update/host-portrait.png" && "max-lg:inset-auto max-lg:top-[-10.72%] max-lg:left-[-13.24%] max-lg:w-[116.67%] max-lg:h-[129.98%] max-lg:max-w-none max-lg:object-fill")}
                />
              </picture>
            )}
          </div>
        </div>

        {/* Left column */}
        <div className={cn("flex w-full flex-col gap-[2.25rem] lg:order-1 lg:w-[75.75rem] lg:gap-[3rem]", monthly && "max-lg:gap-[calc(2.25*var(--page-rem))]")}>
          <div className="hidden flex-col gap-[3rem] lg:flex">
            {data.s4BadgeText && (
              <div className={cn("flex w-fit items-center gap-[1.25rem] border border-black/[0.04] bg-black/[0.04] px-[2.5rem] py-[1.5rem]", monthly && "py-0 lg:h-[4.25rem] lg:px-[calc(2.5rem-1px)] max-lg:h-[calc(4.25*var(--page-rem))] max-lg:px-[calc(2.5*var(--page-rem)-1px)] max-lg:gap-[calc(1.25*var(--page-rem))]")}>
                <span className="h-[0.75rem] w-[0.75rem] rounded-full bg-[#1b8869]" />
                <p className={cn("mb-0 font-geist text-[1.75rem] leading-[1.15] font-medium whitespace-nowrap text-black/[0.64] uppercase", monthly && "[text-box-trim:trim-both] [text-box-edge:cap_alphabetic] max-lg:text-[calc(1.75*var(--page-rem))]")}>
                  {data.s4BadgeText}
                </p>
              </div>
            )}
            {(data.s4TitleRegular || data.s4TitleItalic) && (
              <h2 className="mb-0 w-[67.125rem] font-blacker text-[6rem] leading-[1.15] font-normal tracking-[-0.3rem] text-black [&_span]:font-blacker">
                {data.s4TitleRegular && <span>{data.s4TitleRegular} </span>}
                {data.s4TitleItalic && (
                  <span className={cn("leading-none tracking-[-0.18rem] text-[#3a5842] italic", monthly && "lg:tracking-[-0.3rem] lg:leading-[1.15]")}>{data.s4TitleItalic}</span>
                )}
              </h2>
            )}
          </div>

          {!!hostFacts.length && (
            <div className={"flex w-full flex-col gap-[2rem]"}>
              {hostFacts.map((fact, i) => (
                <div
                  key={fact._key ?? i}
                  className="contents">
                  {i > 0 && <div className={cn("w-full border-t border-black/10", i >= 5 && "lg:hidden", monthly && "max-lg:relative max-lg:h-0 max-lg:border-t-0 max-lg:before:absolute max-lg:before:content-[''] max-lg:before:inset-[-1px_0_auto] max-lg:before:border-t max-lg:before:border-black/10")} />}
                  <div className={cn("flex w-full items-center gap-[4.875rem]", i >= 5 && "lg:hidden")}>
                    <span className={"flex h-[3.25rem] w-[3.25rem] shrink-0 items-center justify-center"}>
                      {fact.icon?.src && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={fact.icon.src}
                          alt=""
                          className={"h-[3.25rem] w-[3.25rem]"}
                        />
                      )}
                    </span>
                    <HostFactText
                      blocks={fact.text}
                      className={cn(HOST_FACT_WIDTHS[i], monthly && "max-lg:text-[calc(2.25*var(--page-rem))]")}
                    />
                  </div>
                </div>
              ))}
              <div className={cn("w-full border-t border-black/10", monthly && "max-lg:relative max-lg:h-0 max-lg:border-t-0 max-lg:before:absolute max-lg:before:content-[''] max-lg:before:inset-[-1px_0_auto] max-lg:before:border-t max-lg:before:border-black/10")} />
            </div>
          )}

          <TicketCTA
            data={data.s4Cta}
            layout={layout}
            iconClassName={monthly ? "max-lg:h-[calc(3.25*var(--page-rem))] max-lg:w-[calc(3.25*var(--page-rem))]" : undefined}
            className={monthly ? "max-lg:gap-[calc(2*var(--page-rem))] max-lg:w-[min(calc(49.75*var(--page-rem)),100%)] max-lg:h-[calc(7.75*var(--page-rem))] lg:w-[min(42.875rem,100%)]" : "h-[7.4rem] w-full lg:h-[7.75rem] lg:w-[47.625rem]"}
          />
        </div>
      </div>
    </section>
  );
}
